/**
 * Monitoring API
 * ===============
 * API client for transaction monitoring and surveillance endpoints.
 */
import apiClient from './client';

// Types
export interface MonitoringFilters {
    agent_id?: string;
    agents?: string[];
    period?: 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'year' | 'custom';
    start_date?: string;
    end_date?: string;
    transaction_types?: string[];
    status?: string;
    min_amount?: number;
    max_amount?: number;
    page?: number;
    page_size?: number;
    group?: boolean;
}

export interface TransactionItem {
    id: string;
    type: string;
    type_display: string;
    status: string;
    formatted_amount: string;
    compte_id: string | null;
    compte_code: string | null;
    client_name: string;
    created_by_id: string | null;
    agent_name: string;
    created_at: string;
    is_grouped: boolean;
    group_count: number;
    details_link: string | null;
}

export interface MonitoringMetadata {
    total_transactions: number;
    total_amount: number;
    total_commission: number;
    transactions_by_type: Record<string, {
        count: number;
        amount: number;
        percentage: number;
    }>;
    period: string;
}

export interface MonitoringResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: {
        transactions: TransactionItem[];
        metadata: MonitoringMetadata;
    };
}

export interface DashboardStats {
    total_transactions: number;
    total_amount: number;
    total_commission: number;
    avg_amount: number;
    transactions_by_type: Record<string, {
        count: number;
        amount: number;
        percentage: number;
    }>;
    transactions_by_status: Record<string, number>;
    daily_trend: Array<{
        date: string;
        count: number;
        amount: number;
    }>;
    hourly_distribution: Array<{
        hour: number;
        count: number;
    }>;
    top_agents?: Array<{
        agent_id: string;
        agent_name: string;
        total_transactions: number;
        total_amount: number;
    }>;
}

export interface RealtimeTransaction {
    id: string;
    type: string;
    type_display: string;
    status: string;
    montant: number;
    formatted_amount: string;
    client_name: string;
    agent_name: string;
    compte_code: string;
    compte_id: string | null;
    created_at: string;
    time: string;
    ago: string;
    count: number;
    sub_transactions?: Array<{
        id: string;
        amount: number;
        time: string;
    }> | null;
}

export interface RealtimeData {
    recent_transactions: RealtimeTransaction[];
    stats: {
        total_count: number;
        total_amount: number;
        last_minutes: number;
    };
    last_updated: string;
}

export interface Agent {
    id: string;
    code: string;
    name: string;
    email: string;
    isActive: boolean;
}

export interface AgentsResponse {
    agents: Agent[];
    total: number;
}

// API Methods
const BASE_URL = '/api/transactions/monitoring';
const AUTH_BASE = '/api/admin';

export const monitoringApi = {
    /**
     * Get list of agents for filter dropdown
     */
    getAgents: async (search?: string): Promise<AgentsResponse> => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        params.append('activeOnly', 'true');
        params.append('_t', Date.now().toString());

        const response = await apiClient.get<AgentsResponse>(`${AUTH_BASE}/agents?${params.toString()}`);
        return response.data;
    },

    /**
     * Get filtered transactions with pagination
     */
    getTransactions: async (filters: MonitoringFilters = {}): Promise<MonitoringResponse> => {
        const params = new URLSearchParams();

        if (filters.agent_id) params.append('agent_id', filters.agent_id);
        if (filters.agents?.length) {
            filters.agents.forEach(a => params.append('agents[]', a));
        }
        if (filters.period) params.append('period', filters.period);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.transaction_types?.length) {
            filters.transaction_types.forEach(t => params.append('transaction_types[]', t));
        }
        if (filters.status) params.append('status', filters.status);
        if (filters.min_amount) params.append('min_amount', String(filters.min_amount));
        if (filters.max_amount) params.append('max_amount', String(filters.max_amount));
        if (filters.page) params.append('page', String(filters.page));
        if (filters.page_size) params.append('page_size', String(filters.page_size));
        if (filters.group !== undefined) params.append('group', String(filters.group));

        const response = await apiClient.get<MonitoringResponse>(`${BASE_URL}/?${params.toString()}`);
        return response.data;
    },

    /**
     * Get dashboard statistics
     */
    getDashboard: async (period: string = 'today', agentId?: string): Promise<DashboardStats> => {
        const params = new URLSearchParams();
        params.append('period', period);
        if (agentId) params.append('agent_id', agentId);

        const response = await apiClient.get<DashboardStats>(`${BASE_URL}/dashboard/?${params.toString()}`);
        return response.data;
    },

    /**
     * Get real-time transaction data
     */
    getRealtime: async (lastMinutes: number = 60, agents?: string[]): Promise<RealtimeData> => {
        const params = new URLSearchParams();
        params.append('last_minutes', String(lastMinutes));
        if (agents?.length) {
            agents.forEach(a => params.append('agents[]', a));
        }

        const response = await apiClient.get<RealtimeData>(`${BASE_URL}/realtime/?${params.toString()}`);
        return response.data;
    },

    /**
     * Export transactions to Excel
     */
    exportToExcel: async (date?: string): Promise<Blob> => {
        const params = new URLSearchParams();
        if (date) params.append('date', date);

        const response = await apiClient.get(`${BASE_URL}/export/?${params.toString()}`, {
            responseType: 'blob',
        });
        return response.data;
    },

    /**
     * Download Excel file
     */
    downloadExcel: async (date?: string): Promise<void> => {
        try {
            const blob = await monitoringApi.exportToExcel(date);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `transactions_${date || new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download Excel:', error);
            throw error;
        }
    },
};

export default monitoringApi;
